class VariantRadios extends HTMLElement {
  constructor() {
    super();
    this.currentVariantLabel = this.querySelector('[data-current-variant-label]');
    this.addEventListener('change', this.onVariantChange);
  }

  // dispatch event on component load - used when these component methods' are needed from another component that's loaded before
  connectedCallback() {
    this.dispatchEvent(new Event('variantsComponentLoaded'));

    // update the options on load (used to display unavailable variants)
    this.updateOptions();
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.updateCurrentVariantLabel();
      this.renderProductInfo();
      this.dispatchVariantChange();
      this.updateProductFormProperties();
    }
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });

    // update Sold out state on options for products with 2 options
    if (this.options.length !== 2) return;
    this.updateSoldOutState(this.options);
  }

  /**
   * Updates the sold-out state of variant options based on current selection (not color options)
   * Works for products with 2 options. For 1 option, it's handled in Liquid. For 3 options, the sold out state is not updated.
   * @param {Array} selectedOptionValues - Array of currently selected option values
   */
  updateSoldOutState(selectedOptionValues = []) {
    const variants = this.getVariantData();
    const filteredVariantsByOption = [];
    const optionSoldOutClass = 'swatches__swatch--unavailable';

    // get the variants that match the selected options
    selectedOptionValues.forEach((optionValue, i) => {
      const optionIndex = i + 1;
      const newVariants = variants.map((variant) => ({ ...variant, optionIndex }));

      const filtered = optionValue ? newVariants.filter((variant) => variant[`option${optionIndex}`] === optionValue) : [];
      filteredVariantsByOption.push(filtered);
    });

    // get the variants that are not available
    const filteredVariantsByAvailability = filteredVariantsByOption.flat().filter((variant) => !variant.available);

    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    const allInputs = fieldsets.map(fieldset => Array.from(fieldset.querySelectorAll('input')));

    // Remove sold out classes from all inputs
    if (fieldsets.length > 1) {
      allInputs.flat().forEach((input) => {
        input.closest('[data-variant-option]').classList.remove(optionSoldOutClass)
      });
    }

    // target the sold out options and add sold out class to its elements
    filteredVariantsByAvailability.forEach(({ optionIndex, options }) => {
      const arrayIndex = optionIndex - 1;
      const newOptions = [...options];
      const newFieldsets = [...fieldsets];

      newOptions.splice(arrayIndex, 1);
      newFieldsets.splice(arrayIndex, 1);

      newFieldsets.forEach((fieldset) => {
        const fields = fieldset.querySelectorAll(`input[value="${newOptions[0]}"]`);

        fields.forEach((field) => field.closest('[data-variant-option]').classList.add(optionSoldOutClass));
      });
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateCurrentVariantLabel() {

    // Check if currentVariant is undefined
    if (!this.currentVariant || !this.currentVariant?.title || !this.currentVariantLabel) return;

    // Update the current variant label element
    this.currentVariantLabel.textContent = this.currentVariant.title.replace('--', ' - ');
  }

  updateMedia() {

    // Check if currentVariant is undefined
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;

    let variantSKU = [];

    product.variants.forEach((productVariant) => {
      variantSKU.push(productVariant.sku);
    });

    // See if the images should be shown or hidden
    this.closest('.product__info-wrapper').querySelectorAll('.product-media__item').forEach((productMediaItem) => {

      if (productMediaItem.classList.contains('product-media__item--swiper')) {
        productMediaItem.closest('.swiper-slide').style.display = 'block';
      } else {
        productMediaItem.classList.remove('visually-hidden');
      }

      if (productMediaItem.querySelector('img[alt*="'+this.currentVariant.sku+'"]')) {

        if (productMediaItem.classList.contains('product-media__item--swiper')) {
          productMediaItem.closest('.swiper-slide').style.display = 'block';
          productMediaItem.setAttribute("data-fancybox", "product-media-mobile");
        } else {
          productMediaItem.classList.remove('visually-hidden');
          productMediaItem.setAttribute("data-fancybox", "product-media");
        }

      } else {
        let hideImage = false;

        variantSKU.forEach((variantSKUValue) => {
          if (productMediaItem.querySelector('img[alt*="'+variantSKUValue+'"]')) {
            hideImage = true;
          }
        });

        if (hideImage) {
          if (productMediaItem.classList.contains('product-media__item--swiper')) {
            productMediaItem.closest('.swiper-slide').style.display = 'none';
            productMediaItem.removeAttribute("data-fancybox", "product-media-mobile");
          } else {
            productMediaItem.classList.add('visually-hidden');
            productMediaItem.removeAttribute("data-fancybox", "product-media");
          }
        }
      }
    });

    this.productSwiperElement = document.querySelector('product-detail-swiper');
    this.productSwiperElement?.updateSwiper();

    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => { parent.scroll(0, 0) });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent(
        'variant-change',
        {
          bubbles: true,
          variant: this.currentVariant,
          detail: {
            variant: this.currentVariant
          }
        }
      )
    );
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('visibility-hidden');
        if (this.currentVariant) {
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
        }
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]');

    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', true);
      if (text) addButton.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButton.textContent = window.variantStrings.addToCart;
      const nextStepButton = document.querySelector('[data-product-next-step]');
      if (nextStepButton) {
        nextStepButton.classList.add('button--disabled');
      }
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]');
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  updateProductFormProperties() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const optionPropsList = productForm.querySelectorAll('[data-properties-option]');
      optionPropsList.forEach(option => {
        if (option) {
          const optionName = option.getAttribute('data-properties-option') || false
          if (optionName) {
            option.value = this.currentVariant[optionName];
            option.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });

    });
  }
}

customElements.define('variant-radios', VariantRadios);
